import React, { useEffect, useState } from 'react';

import { Box, Checkbox, Grid } from '@mui/material';
import {
  AllSeriesType,
  AreaPlot,
  AxisConfig,
  BarPlot,
  ChartsLegend,
  ChartsTooltip,
  ChartsXAxis,
  ChartsYAxis,
  LinePlot,
  MarkPlot,
  ResponsiveChartContainer,
  axisClasses,
} from '@mui/x-charts';
import { MakeOptional } from '@mui/x-charts/models/helpers';

import { ChartData } from '../../@types/KingdomResponse';

interface Props {
  dataSet: ChartData[];
  hasMultiple: boolean;
}

const colors = ['Green', 'Red', 'Green', 'Red'];
interface ValuesByType {
  landValues: number[];
  nwValues: number[];
  honorValues: number[];
}
interface AxisSeries {
  yAxis: MakeOptional<AxisConfig, 'id'>[];
  series: AllSeriesType[];
}

const NwLandGraph: React.FC<Props> = ({ dataSet, hasMultiple }) => {
  const [labels, setLabels] = useState<string[]>(['Land', 'Networth']);
  const [valuesByType, setValuesByType] = useState<ValuesByType>({
    landValues: [],
    nwValues: [],
    honorValues: [],
  });
  const [config, setConfig] = useState<AxisSeries>({
    yAxis: [],
    series: [],
  });

  useEffect(() => {
    const lv = dataSet.map((item) => Number(item.kd1Land));
    const nv = dataSet.map((item) => Number(item.kd1Nw));
    const hv = dataSet.map((item) => Number(item.kd1Honor));

    if (hasMultiple) {
      lv.push(...dataSet.map((item) => Number(item.kd2Land)));
      nv.push(...dataSet.map((item) => Number(item.kd2Nw)));
      hv.push(...dataSet.map((item) => Number(item.kd2Honor)));
    }
    const newValues = {
      landValues: lv,
      nwValues: nv,
      honorValues: hv,
    };
    setValuesByType(newValues);
  }, [dataSet, hasMultiple]);

  useEffect(() => {
    const newAxis: MakeOptional<AxisConfig, 'id'>[] = [];
    const newSeries: AllSeriesType[] = [];
    newAxis.push({
      id: 'landAxis',
      min: Math.min(...valuesByType.landValues) * 0.95,
      max: Math.max(...valuesByType.landValues) * 1.1,
    });
    newSeries.push({
      dataKey: 'kd1Land',
      type: 'line',
      label: 'KD1 Land',
      yAxisKey: 'landAxis',
      showMark: true,
      area: false,
      color: colors[0],
    });
    if (hasMultiple) {
      newSeries.push({
        dataKey: 'kd2Land',
        type: 'line',
        label: 'KD2 Land',
        showMark: true,
        area: false,
        yAxisKey: 'landAxis',
        color: colors[1],
      });
    }
    if (labels.includes('Networth')) {
      newAxis.push({
        id: 'nwAxis',
        min: Math.min(...valuesByType.nwValues) * 0.8,
        max: Math.max(...valuesByType.nwValues) * 1.05,
      });
      newSeries.push({
        dataKey: 'kd1Nw',
        type: 'line',
        label: 'KD1 Nw',
        yAxisKey: 'nwAxis',
        area: false,
        showMark: false,
        color: colors[0],
      });
      if (hasMultiple) {
        newSeries.push({
          dataKey: 'kd2Nw',
          type: 'line',
          label: 'KD2 Nw',
          yAxisKey: 'nwAxis',
          area: false,
          showMark: false,
          color: colors[1],
        });
      }
    }
    if (labels.includes('Honor')) {
      newAxis.push({
        id: 'honorAxis',
        min: Math.min(...valuesByType.honorValues) * 0.8,
        max: Math.max(...valuesByType.honorValues) * 1.05,
      });
      newSeries.push({
        dataKey: 'kd1Honor',
        type: 'line',
        label: 'KD1 Honor',
        yAxisKey: 'honorAxis',
        area: false,
        showMark: false,
        color: colors[0],
      });
      if (hasMultiple) {
        newSeries.push({
          dataKey: 'kd2Honor',
          type: 'line',
          label: 'KD2 Honor',
          yAxisKey: 'honorAxis',
          area: false,
          showMark: false,
          color: colors[1],
        });
      }
    }
    setConfig({ yAxis: newAxis, series: newSeries });
  }, [hasMultiple, labels, valuesByType]);

  const handleLabelClick = (label: string) => {
    if (labels.includes(label)) {
      const newLabels = labels.filter((x) => x !== label);
      setLabels(newLabels);
    } else {
      const newLabels = [...labels];

      if (newLabels.length === 2) {
        newLabels.pop();
      }
      newLabels.push(label);
      setLabels(newLabels);
    }
  };

  const hasOne = labels.length === 1 && labels.length === config.yAxis.length;
  const hasNw =
    labels.length === config.yAxis.length &&
    labels[1] === 'Networth' &&
    !!config.yAxis.find((x) => x.id === 'nwAxis');
  const hasHonor =
    labels.length === config.yAxis.length &&
    labels[1] === 'Honor' &&
    !!config.yAxis.find((x) => x.id === 'honorAxis');
  return (
    <Box
      sx={{
        marginTop: 2,
        marginBottom: 2,
      }}
    >
      <Grid container sx={{ justifyContent: 'center' }}>
        <Grid item sx={{ px: 1, textAlign: 'center' }}>
          <Box style={{ fontSize: '10px' }}>Networth</Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              size='small'
              checked={labels.includes('Networth')}
              onClick={() => handleLabelClick('Networth')}
            />
          </Box>
        </Grid>
        <Grid item sx={{ px: 1, textAlign: 'center' }}>
          <Box style={{ fontSize: '10px' }}>Honor</Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Checkbox
              size='small'
              checked={labels.includes('Honor')}
              onClick={() => handleLabelClick('Honor')}
            />
          </Box>
        </Grid>
      </Grid>

      {(hasOne || hasNw || hasHonor) && (
        <ResponsiveChartContainer
          height={450}
          margin={{ top: 60, bottom: 40, left: 70, right: 80 }}
          dataset={dataSet}
          series={config.series}
          xAxis={[
            {
              dataKey: 'tick',
              id: 'x-axis-id',
              scaleType: 'linear',
            },
          ]}
          yAxis={config.yAxis}
          sx={{
            [`.${axisClasses.left} .${axisClasses.label}`]: {
              transform: 'translate(-25px, 0)',
            },
            [`.${axisClasses.right} .${axisClasses.label}`]: {
              transform: 'translate(40px, 0)',
            },
          }}
        >
          <LinePlot />
          <BarPlot />
          <MarkPlot />
          <AreaPlot />
          <ChartsTooltip />
          <ChartsXAxis label='Ticks' position='bottom' axisId='x-axis-id' />
          <ChartsYAxis label='Land' position='left' axisId='landAxis' />
          {labels.includes('Networth') && (
            <ChartsYAxis
              label='NW'
              position={
                labels.includes('Networth') && labels.includes('Honor')
                  ? 'left'
                  : 'right'
              }
              axisId='nwAxis'
            />
          )}
          {labels.includes('Honor') && (
            <ChartsYAxis label='Honor' position='right' axisId='honorAxis' />
          )}
          <ChartsLegend
            direction='row'
            position={{ vertical: 'top', horizontal: 'middle' }}
          />
        </ResponsiveChartContainer>
      )}
    </Box>
  );
};

export default NwLandGraph;
